import { useRef, useState } from "react";
import {
  IonModal, IonHeader, IonToolbar, IonButtons, IonButton, IonIcon, IonTitle,
  IonFooter, IonTextarea, IonProgressBar,
} from "@ionic/react";
import { closeOutline, closeCircleOutline, imageOutline, sendOutline } from "ionicons/icons";
import FreshOverlay from "@/components/FreshOverlay";
import ImageLightbox from "@/components/ImageLightbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// The comment thread on a signature request. The composer is the admin
// support chat's, exactly: the "Write your message here" box, image
// attachments, and the green send button with the success progress
// strip while it works.
const fmt = (at) => {
  try {
    return new Date(at).toLocaleString("en-US", { month: "numeric", day: "numeric", year: "2-digit", hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onloadend = () => resolve(String(r.result));
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function CommentsThreadModal({ isOpen, onClose, comments = [], value, onValue, busy, onSubmit, dark, readOnly }) {
  const fileInputRef = useRef(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const handleImageAdd = (e) => {
    const files = Array.from(e.target.files || []);
    setImageFiles((prev) => [...prev, ...files]);
    files.forEach((f) => {
      const url = URL.createObjectURL(f);
      setImagePreviews((prev) => [...prev, url]);
    });
    e.target.value = "";
  };
  const handleImageRemove = (idx) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== idx));
    setImagePreviews((prev) => {
      URL.revokeObjectURL(prev[idx]);
      return prev.filter((_, i) => i !== idx);
    });
  };
  const clearImages = () => {
    imagePreviews.forEach((u) => URL.revokeObjectURL(u));
    setImageFiles([]);
    setImagePreviews([]);
  };

  const submit = async () => {
    if (busy || (!String(value || "").trim() && imageFiles.length === 0)) return;
    const images = await Promise.all(imageFiles.map(fileToDataUrl));
    const ok = await onSubmit(images);
    if (ok !== false) clearImages();
  };
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  const imgSrc = (u) => (String(u).startsWith("data:") ? u : `${BACKEND_URL}${u}`);

  return (
    <FreshOverlay as={IonModal} isOpen={isOpen} onDidDismiss={() => { clearImages(); onClose(); }} className={"comments-modal" + (dark ? " comments-dark" : "")}>
      <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            {/* the slotted icon inherits from the ion-button HOST, which Ionic's
                toolbar styles repaint - so the color rides inline on both */}
            <IonButton onClick={onClose} aria-label="Close"
              style={{ "--color": dark ? "#ffffff" : "var(--ion-text-color, #0d1b2a)", color: dark ? "#ffffff" : "var(--ion-text-color, #0d1b2a)" }}>
              <IonIcon slot="icon-only" icon={closeOutline} style={{ fontSize: 28, color: dark ? "#ffffff" : "var(--ion-text-color, #0d1b2a)" }} />
            </IonButton>
          </IonButtons>
          <IonTitle>Comments</IonTitle>
        </IonToolbar>
      </IonHeader>
      <div className="comments-scroll">
        <div className="comments-thread">
          {comments.length === 0 ? (
            // the Invoices modal's empty state: the No-results look (size,
            // weight, colour), icon-less, centred in the thread's space
            <div className="comments-empty" style={{ flex: 1, minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px", color: "var(--ion-text-color)" }}>
              <div style={{ fontSize: "1.4rem", fontWeight: 700, textAlign: "center", lineHeight: 1.2, wordBreak: "break-word" }}>
                No comments yet. Start the conversation.
              </div>
            </div>
          ) : comments.map((c) => (
            <div key={c.id} className="comment-row">
              <div className="comment-avatar">{((c.name || "?").trim()[0] || "?").toUpperCase()}</div>
              <div style={{ minWidth: 0 }}>
                <p className="comment-head"><strong>{c.name || "Someone"}</strong> <span>{fmt(c.at)}</span></p>
                {c.text ? <p className="comment-text">{c.text}</p> : null}
                {c.images?.length > 0 && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 6 }}>
                    {c.images.map((url, i) => (
                      <img key={i} src={imgSrc(url)} alt="" onClick={() => setLightboxSrc(imgSrc(url))}
                        style={{ width: 120, height: 90, objectFit: "cover", borderRadius: 8, border: "1px solid var(--ion-border-color)", cursor: "zoom-in" }} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {!readOnly && (
      <IonFooter>
        {/* the support chat's input area, verbatim */}
        <div className="comment-composer" style={{ borderTop: busy ? "none" : "1px solid var(--ion-border-color)", flexShrink: 0 }}>
          {busy && <IonProgressBar type="indeterminate" color="success" style={{ height: 2 }} />}

          {imagePreviews.length > 0 && (
            <div style={{ display: "flex", gap: 8, padding: "8px 16px 0", flexWrap: "wrap" }}>
              {imagePreviews.map((url, i) => (
                <div key={i} style={{ position: "relative", width: 72, height: 72 }}>
                  <img src={url} alt="" style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: "1px solid var(--ion-border-color)" }} />
                  <IonButton
                    fill="clear" size="small" color="danger"
                    style={{ position: "absolute", top: -6, right: -6, "--padding-start": "2px", "--padding-end": "2px", "--padding-top": "2px", "--padding-bottom": "2px" }}
                    onClick={() => handleImageRemove(i)}
                  >
                    <IonIcon slot="icon-only" icon={closeCircleOutline} style={{ fontSize: 18 }} />
                  </IonButton>
                </div>
              ))}
            </div>
          )}

          <div className="msg-input" onKeyDown={handleKeyDown} style={{ padding: "4px 8px 0" }}>
            <IonTextarea
              autoGrow
              rows={3}
              placeholder="Write your message here"
              value={value}
              onIonInput={(e) => onValue(e.detail.value ?? "")}
              style={{
                "--background": "transparent",
                "--padding-start": "16px",
                "--highlight-height": "0px",
              }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 8px 8px" }}>
            <div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={handleImageAdd} />
              <IonButton fill="clear" color="medium" size="small" style={{ "--border-radius": "50%" }} onClick={() => fileInputRef.current?.click()}>
                <IonIcon slot="icon-only" icon={imageOutline} style={{ fontSize: 20 }} />
              </IonButton>
            </div>
            <IonButton
              size="small"
              color="success"
              className="comment-send-btn"
              disabled={busy || (!String(value || "").trim() && imageFiles.length === 0)}
              onClick={submit}
              style={{ "--color": "#ffffff", "--border-radius": "4px" }}
            >
              <IonIcon slot="start" icon={sendOutline} style={{ fontSize: 14 }} />
              <span style={{ fontWeight: 700, letterSpacing: "0.05em", fontSize: "0.8rem" }}>ADD COMMENT</span>
            </IonButton>
          </div>
        </div>
      </IonFooter>
      )}
      </div>
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </FreshOverlay>
  );
}
